import type {
  BrandAppData,
  BrandBilling,
  BrandCampaign,
  BrandCampaignAccountsResponse,
  BrandCampaignFileListResponse,
  BrandCampaignFileResponse,
  BrandCandidatesResponse,
  BrandCreatorProfile,
  BrandCreatorRatings,
  BrandInviteResponse,
  BrandPerformance,
  BrandPlanKey,
  BrandPlanState,
  BrandProduct,
  BrandProductRequest,
  BrandProfile,
  BrandProfileRequest,
  BrandRatingRequest,
  CampaignInput,
  PerformanceRange,
  ProductLookup,
  BrandReviewRequest,
  BrandReviewResponse,
  RateSuggestion,
  RateSuggestionRequest,
} from '../../shared/brand-api';
import type {Message} from '../../shared/api';
import type {StoredFile} from '../../shared/creator-api';
import type {ApiKeyAccess, ApiKeyCreatedResponse, ApiKeyListResponse} from '../../shared/api-keys';
import type {BrandTeamRequest, BrandTeamResponse} from '../../shared/brand-team';

const BASE = '/api/brand';

export class BrandApiError extends Error {
  constructor(readonly status: number, readonly code: string, readonly fields: Record<string, string> = {}) {
    super(code);
  }
}

const FRIENDLY: Record<string, string> = {
  campaign_not_live: 'You can invite creators once the campaign is live.',
  not_invitable: 'That creator can no longer be invited to this campaign. The list is up to date now.',
  invite_limit: 'Twenty invites are already waiting for an answer. Wait for some to answer, or cancel one.',
  invite_not_pending: 'That invite was already answered or has lapsed.',
  invite_declined: 'This creator said no to this campaign, so they are not asked again.',
  invite_recent: 'You cancelled this invite less than a day ago. You can send it again tomorrow.',
  confirmation_required: 'Type REMOVE to confirm this action.',
  creator_work_pending: 'Decide or finish this creator’s open videos before removing them.',
  creator_not_in_campaign: 'This creator is no longer in the campaign. The page is up to date now.',
  rating_not_allowed: 'You can rate a creator once you have approved one of their videos.',
  invalid_stars: 'Choose one to five stars.', comment_too_long: 'Keep the comment to 280 characters.',
  comment_off_platform: 'Leave contact details and links out of the comment: other brands read it.',
  unauthorized: 'Your session has ended. Sign in again.', invalid_email: 'Enter a valid work email.',
  invalid_link: 'That link has expired or was already used.', invalid_code: 'That code is not right or has expired.',
  email_not_configured: 'Sign-in email is not configured yet.', email_failed: 'We could not send the email. Try again shortly.',
  rate_limited: 'Too many attempts. Please wait and try again.', invalid_fields: 'Check the highlighted fields.',
  profile_incomplete: 'Complete your company details first.', acceptance_required: 'Accept both confirmations to continue.',
  signature_mismatch: 'Type your name exactly as it appears in Company details.', agreement_required: 'Sign the agreement before creating a campaign.',
  plan_required: 'Choose a plan before you start a campaign.',
  product_required: 'Add the product this campaign is for first.',
  last_product: 'Keep at least one product: every campaign is for one.',
  lookup_failed: 'The store didn’t tell us about this app. Fill it in yourself.',
  not_an_image: 'Use a PNG, JPEG or WebP image.',
  plan_limit: 'Your plan’s active campaigns are all in use. Upgrade to publish another one.',
  campaign_locked: 'This campaign is already live or otherwise locked. Contact the Earn team for changes.',
  visibility_locked: 'Visibility can be changed while a campaign is live or paused.',
  campaign_in_use: 'Creators are already on this campaign, so it can’t be deleted. Finish it instead.',
  campaign_state: 'This campaign changed meanwhile. The page is up to date now.',
  paused_by_team: 'The Earn team paused this campaign. Write to us to resume it.',
  publish_failed: 'The campaign was saved, but could not go live. Try publishing it again.',
  access_required: 'You do not have access to that conversation.', empty_message: 'Write a message before sending.',
  not_pending: 'This video was already decided, or our team took it back. The list is up to date now.',
  not_found: 'That video is no longer waiting for you.', brand_paused: 'Your workspace is paused. Contact the Earn team.',
  job_full: 'Every spot on this campaign is taken.', not_pickable: 'That creator can no longer be picked. The list is up to date now.',
  wrong_mode: 'This campaign takes the first creators to reserve, so there is nobody to pick.',
  files_unavailable: 'File uploads are temporarily unavailable. Try again shortly.',
  empty_file: 'Choose a non-empty file.', file_too_large: 'Each file can be up to 100 MB.',
  upload_failed: 'That file could not be uploaded. Try again.',
  storage_full: 'This creator’s shared file workspace is full.',
  message_file_limit: 'A message can include up to 5 files.',
  file_in_use: 'Remove this file from the brief and save the campaign before deleting it.',
  file_limit: 'This campaign already has 12 files. Remove an unused file before uploading another.',
  key_limit: 'You have 10 live keys. Revoke one you no longer use first.',
  off_platform_contact: 'Contact details, outside links and payment details can’t be shared with creators. Keep the collaboration on Earn.',
  objectionable_content: 'This message breaks the community rules, so it was not sent.',
  payments_unavailable: 'Online payment is not set up yet. Our team will invoice you directly.',
  credit_required: 'Add credit first: each creator you bring in sets aside the price of their videos.',
  credit_negative: 'Your credit is below zero. Add credit first.',
  payment_unavailable: 'The payment page could not be opened. Try again in a minute.',
  bank_payment_unavailable: 'Amounts above $5,000 are paid by bank, and bank payment isn’t available yet. Choose a smaller amount, or contact us for an invoice.',
  nothing_paid_yet: 'There is nothing to manage yet. Your invoices appear here after your first payment.',
  admin_only: 'Only an Admin of your team can do this. Ask one of them.',
  already_member: 'This person is already on your team.',
  email_taken: 'This address already signs in to another brand workspace. Invite another address.',
  team_full: 'Your team is full. Remove someone first.',
  own_membership: 'Ask another Admin to change your own role or access.',
  already_joined: 'They have joined already.',
  member_not_found: 'That person is no longer on your team. The list is up to date now.',
  owner_stays: 'The owner who registered the workspace stays on the team.',
};

export function describe(error: unknown): string {
  return error instanceof BrandApiError ? FRIENDLY[error.code] || 'Something went wrong. Please try again.' : 'Something went wrong. Please try again.';
}

async function request<T extends object>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;
  try { response = await fetch(path, init); }
  catch { throw new BrandApiError(0, 'network'); }
  const payload = await response.json().catch(() => null) as ({ok: boolean; error?: string; errors?: Record<string, string>} & T) | null;
  if (response.ok && payload?.ok) return payload;
  throw new BrandApiError(response.status, payload?.error || 'request_failed', payload?.errors);
}

const post = (body: unknown): RequestInit => ({method: 'POST', headers: {'content-type': 'application/json'}, body: JSON.stringify(body)});

export const fetchBrand = () => request<BrandAppData>(BASE);
export const requestLogin = (email: string) => request<{dev_link?: string; dev_code?: string}>(`${BASE}/auth/request`, post({email}));
/** Whose a sign-in link is (masked) and whether it is the workspace open here, without using it. */
export const peekLogin = (token: string) => request<{email: string; current: boolean}>(`${BASE}/auth/peek`, post({token}));
export const redeemLogin = (token: string) => request<Record<string, never>>(`${BASE}/auth/redeem`, post({token}));
export const verifyLogin = (email: string, code: string) => request<Record<string, never>>(`${BASE}/auth/verify`, post({email, code}));
export const signOut = () => request<Record<string, never>>(`${BASE}/logout`, post({}));
export const saveProfile = (profile: BrandProfileRequest) => request<{profile: BrandProfile; stage: string}>(`${BASE}/profile`, post(profile));
export const signAgreement = (signature: string, acceptTerms: boolean, acceptPlatform: boolean) =>
  request<Record<string, never>>(`${BASE}/agreement`, post({signature, accept_terms: acceptTerms, accept_platform: acceptPlatform}));
/** Records the plan the brand chose; its payment link, when there is one, is opened by the page itself. */
/** With Stripe set up, a first plan answers with the Checkout to pay it in; the plan is set once it is paid. */
export const choosePlan = (plan: BrandPlanKey) => request<{plan: BrandPlanState; checkout_url?: string}>(`${BASE}/plan`, post({plan}));
export const fetchBilling = () => request<{billing: BrandBilling}>(`${BASE}/billing`);
export const markGiftsSeen = (ids: string[]) => request<Record<string, never>>(`${BASE}/credit/gifts/seen`, post({ids}));
/** `back`: the campaign page to return to once the payment lands (credit-dialog.tsx). */
export const startTopUp = (amountCents: number, back = '') => request<{url: string}>(`${BASE}/billing/top-up`, post({amount_cents: amountCents, ...(back ? {back} : {})}));
export const openBillingPortal = () => request<{url: string}>(`${BASE}/billing/portal`, post({}));
/** Adds a product, or edits one (`id`); a new name reaches its campaigns at once. Answers the list as it is now. */
export const saveProduct = (id: string | null, product: BrandProductRequest) =>
  request<{product: BrandProduct; products: BrandProduct[]}>(id ? `${BASE}/products/${id}` : `${BASE}/products`, post(product));
/** Takes a product off the list; its campaigns keep it. */
export const removeProduct = (id: string) => request<{products: BrandProduct[]}>(`${BASE}/products/${id}/remove`, post({}));
export const uploadProductIcon = (id: string, file: File) => request<{product: BrandProduct; products: BrandProduct[]}>(
  `${BASE}/products/${id}/icon`, {method: 'PUT', headers: {'content-type': file.type || 'application/octet-stream'}, body: file});
/** What an App Store or Google Play link says about the app: its name and icon. */
export const lookupProduct = (url: string) => request<{lookup: ProductLookup}>(`${BASE}/products/lookup`, post({url}));
export const suggestRate = (spec: RateSuggestionRequest) => request<RateSuggestion>(`${BASE}/rate`, post(spec));
export const createCampaign = (input: CampaignInput) => request<{campaign: BrandCampaign}>(`${BASE}/campaigns`, post(input));
export const saveCampaign = (id: string, input: CampaignInput) => request<{campaign: BrandCampaign}>(`${BASE}/campaigns/${id}`, post(input));
/** Publishes a complete draft immediately; campaign review is not a gate. */
export const submitCampaign = (id: string) =>
  request<{campaign: BrandCampaign; live: boolean}>(`${BASE}/campaigns/${id}/submit`, post({}));
/** Pauses, resumes or finishes a live campaign (surfaces/brand/campaign-run.ts). */
export const runCampaign = (id: string, action: 'pause' | 'resume' | 'finish') =>
  request<{campaign: BrandCampaign}>(`${BASE}/campaigns/${id}/${action}`, post({}));
/** Makes a live or paused campaign public or invite-only. */
export const setCampaignVisibility = (id: string, accessMode: 'open' | 'invited') =>
  request<{campaign: BrandCampaign}>(`${BASE}/campaigns/${id}/visibility`, post({access_mode: accessMode}));
/** Deletes a campaign no creator has come near (BrandCampaign.deletable). */
export const deleteCampaign = (id: string) => request<{deleted: string}>(`${BASE}/campaigns/${id}/delete`, post({}));
export const loadCampaignFiles = (id: string) => request<BrandCampaignFileListResponse>(`${BASE}/campaigns/${id}/files`);
export const uploadCampaignFile = (id: string, file: File) => request<BrandCampaignFileResponse>(
  `${BASE}/campaigns/${id}/files?name=${encodeURIComponent(file.name)}`,
  {method: 'PUT', headers: {'content-type': file.type || 'application/octet-stream'}, body: file},
);
export const deleteCampaignFile = (campaignId: string, fileId: string) =>
  request<{deleted: string}>(`${BASE}/campaigns/${campaignId}/files/${fileId}`, {method: 'DELETE'});
/** `held`: the off-platform guard kept the message for the team; the creator has not seen it. */
export const sendMessage = (accountId: string, projectId: string, body: string) =>
  request<{message: Message; held?: boolean}>(`${BASE}/messages`, post({account_id: accountId, project_id: projectId, body}));
/** Adds a file to a sent message and to this creator's shared Files workspace. */
export const uploadMessageFile = (messageId: string, file: File) => request<{file: StoredFile}>(
  `${BASE}/messages/${messageId}/files?name=${encodeURIComponent(file.name)}`,
  {method: 'PUT', headers: {'content-type': file.type || 'application/octet-stream'}, body: file},
);
export const messageFileUrl = (messageId: string, fileId: string) => `${BASE}/messages/${messageId}/files/${fileId}`;
export const markRead = (accountId: string, projectId: string) =>
  request<Record<string, never>>(`${BASE}/messages`, post({account_id: accountId, project_id: projectId, action: 'read'}));

/** The brand's answer on one video: a yes with what it liked, or the changes it needs. */
export const decideReview = (deliverableId: string, decision: BrandReviewRequest) =>
  request<BrandReviewResponse>(`${BASE}/deliverables/${deliverableId}/review`, post(decision));

/** Picks an applicant of a campaign that picks its creators; answers with the campaign as it is now. */
export const pickApplicant = (campaignId: string, accountId: string) =>
  request<{campaign: BrandCampaign}>(`${BASE}/campaigns/${campaignId}/pick`, post({account_id: accountId}));

/** A creator the brand met on one of its campaigns: photos, earlier work and their accounts. */
export const fetchCreatorProfile = (accountId: string) => request<{creator: BrandCreatorProfile}>(`${BASE}/creators/${accountId}`);
/**
 * Where one of that creator's photos or reference videos loads from (checked
 * like the profile). A photo drawn small asks for the Worker's smaller copy at
 * `width` (services/thumbnails.ts): 128 for an avatar, 480 for a card or tile.
 */
export const creatorFileUrl = (accountId: string, fileId: string, width?: 128 | 480) =>
  `${BASE}/creators/${accountId}/files/${fileId}${width ? `?w=${width}` : ''}`;
/**
 * The one picture the brand sees a creator by, wherever it shows them: their
 * profile picture, else their TikTok's. It is checked like the profile, and a
 * 404 draws their initials. `photoId` makes a new profile picture a new address.
 */
export const creatorPictureUrl = (accountId: string, width: 128 | 480, photoId?: string | null) =>
  `${BASE}/creators/${accountId}/picture?w=${width}${photoId ? `&v=${photoId}` : ''}`;
/** What the shared Person (apps/shared/person) draws one creator from, wherever the portal shows them. */
export const creatorPicture = (creator: {account_id: string; photo_file_id: string | null}, width: 128 | 480 = 128) =>
  [creatorPictureUrl(creator.account_id, width, creator.photo_file_id)];
/** Creators the campaign is open to who have not applied, the better ones first. */
export const fetchCandidates = (campaignId: string, search: string, offset: number) =>
  request<BrandCandidatesResponse>(`${BASE}/campaigns/${campaignId}/candidates?q=${encodeURIComponent(search)}&offset=${offset}`);
export const inviteCreator = (campaignId: string, accountId: string) =>
  request<BrandInviteResponse>(`${BASE}/campaigns/${campaignId}/invite`, post({account_id: accountId}));
export const revokeCreatorInvite = (campaignId: string, accountId: string) =>
  request<BrandInviteResponse>(`${BASE}/campaigns/${campaignId}/invite/revoke`, post({account_id: accountId}));
/** Ends the creator's place and access after an explicit typed confirmation. */
export const removeCampaignCreator = (campaignId: string, accountId: string) =>
  request<{campaign: BrandCampaign}>(`${BASE}/campaigns/${campaignId}/remove`, post({account_id: accountId, confirmation: 'REMOVE'}));
/** The brand's rating of a creator it approved a video from; answers the creator's ratings as the profile shows them. */
export const rateCreator = (accountId: string, rating: BrandRatingRequest) =>
  request<{ratings: BrandCreatorRatings}>(`${BASE}/creators/${accountId}/rating`, post(rating));

/** Where the brand downloads a file its campaign's brief hands to creators. */
export const briefFileUrl = (campaignId: string, fileId: string) => `${BASE}/campaigns/${campaignId}/files/${fileId}?download=1`;

/** The campaign's posted videos with the views we captured, and the totals. */
/** What the posted videos did in a range of days, on every campaign or one (shared/brand-api.ts BrandPerformance). */
export const fetchPerformance = (range: PerformanceRange, campaignId: string | null) =>
  request<BrandPerformance>(`${BASE}/performance?${new URLSearchParams({range, ...(campaignId ? {campaign: campaignId} : {})})}`);

/** The TikTok accounts a canvas campaign's creators post on. */
export const fetchCampaignAccounts = (campaignId: string) => request<BrandCampaignAccountsResponse>(`${BASE}/campaigns/${campaignId}/accounts`);

/** The keys the brand's AI agents and scripts use; the secret comes back once, on creation. */
export const fetchApiKeys = () => request<ApiKeyListResponse>(`${BASE}/api-keys`);
export const createApiKey = (name: string, access: ApiKeyAccess) =>
  request<ApiKeyCreatedResponse>(`${BASE}/api-keys`, post({action: 'create', name, access}));
export const revokeApiKey = (id: string) => request<Record<string, never>>(`${BASE}/api-keys`, post({action: 'revoke', id}));

/** Who is on the brand's team; an Admin invites, changes a role, sends an invitation again or removes someone. */
export const fetchTeam = () => request<BrandTeamResponse>(`${BASE}/team`);
export const changeTeam = (change: BrandTeamRequest) =>
  request<BrandTeamResponse & {sent?: boolean; dev_link?: string}>(`${BASE}/team`, post(change));
